/**
 * Score Delta Badge Component
 * Displays score changes for individual dimensions with appropriate colors
 */

import React from 'react';

export type ScoreDimension = 'brain' | 'heart' | 'task' | 'comm';

interface ScoreDeltaBadgeProps {
  dimension: ScoreDimension;
  delta: number;
  showZero?: boolean;
  size?: 'small' | 'medium';
}

const dimensionConfig: Record<ScoreDimension, { label: string; emoji: string }> = {
  brain: { label: 'Brain', emoji: '🧠' },
  heart: { label: 'Heart', emoji: '❤️' },
  task: { label: 'Task', emoji: '📋' },
  comm: { label: 'Comm', emoji: '💬' }
};

export const ScoreDeltaBadge: React.FC<ScoreDeltaBadgeProps> = ({
  dimension,
  delta,
  showZero = false,
  size = 'small'
}) => {
  if (delta === 0 && !showZero) {
    return null;
  }

  const config = dimensionConfig[dimension];
  const isPositive = delta > 0;
  const isNegative = delta < 0;

  const baseClasses = size === 'small'
    ? 'px-2 py-0.5 text-xs font-medium rounded'
    : 'px-3 py-1 text-sm font-medium rounded-md';

  const colorClasses = isPositive
    ? 'bg-green-100 text-green-800'
    : isNegative
    ? 'bg-red-100 text-red-800'
    : 'bg-gray-100 text-gray-600';

  const sign = isPositive ? '+' : '';

  return (
    <span className={`inline-flex items-center gap-1 ${baseClasses} ${colorClasses}`}>
      <span>{sign}{delta}</span>
      <span>{config.emoji}</span>
    </span>
  );
};

interface ScoreDeltaGroupProps {
  deltas: {
    brain: number;
    heart: number;
    task: number;
    comm: number;
  } | null;
  showZero?: boolean;
  size?: 'small' | 'medium';
}

/**
 * Group of score delta badges for displaying all dimensions at once
 */
export const ScoreDeltaGroup: React.FC<ScoreDeltaGroupProps> = ({
  deltas,
  showZero = false,
  size = 'small'
}) => {
  if (!deltas) {
    return null;
  }

  const dimensions: ScoreDimension[] = ['brain', 'heart', 'task', 'comm'];
  const hasAnyImpact = dimensions.some(d => deltas[d] !== 0);

  if (!hasAnyImpact && !showZero) {
    return (
      <span className="text-xs text-gray-500 italic">No score impact</span>
    );
  }

  return (
    <div className="flex flex-wrap gap-1.5">
      {dimensions.map(dim => (
        <ScoreDeltaBadge
          key={dim}
          dimension={dim}
          delta={deltas[dim]}
          showZero={showZero}
          size={size}
        />
      ))}
    </div>
  );
};

export default ScoreDeltaBadge;
